import React, { useState } from "react";
import {
  Modal,
  Typography,
  Box,
  CircularProgress,
  Button,
  Divider,
  Alert,
} from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { startVerificationSession } from "../utils/userUtils";
import DataSourcesPopupModal from "./DataSourcesPopupModal";
import OrganizationVerificationModal from "./OrganizationVerificationModal";
import ManualVerificationModal from "./ManualVerificationModal";
import { useUser } from "../context/UserContext";
import { isPoliticianAccount } from "../utils/accountTypes";

const COMMUNITY_ENGAGEMENT_HINT =
  "Verify your identity to unlock your verified badge and full civic profile.";

const VerifyIdentityModal = ({ isOpen, onClose, communityEngagement = false }) => {
  const { accountTypeName, isStripeVerified, politicianVerificationStatus, citizenManualVerificationStatus } = useUser();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [showManualVerification, setShowManualVerification] = useState(false);

  const isOrgAccount = ["organization", "non_profit"].includes(accountTypeName || "");
  const isPolitician = isPoliticianAccount(accountTypeName);
  const politicianPending = isPolitician && isStripeVerified && ["pending", "unsubmitted"].includes(politicianVerificationStatus);
  const citizenPending = !isPolitician && !isOrgAccount && citizenManualVerificationStatus === "pending";
  const verificationPending = politicianPending || citizenPending;

  if (isOrgAccount && isOpen) {
    return <OrganizationVerificationModal isOpen={isOpen} onClose={onClose} />;
  }

  const handleVerify = async () => {
    setError(null);
    setLoading(true);

    try {
      const { url } = await startVerificationSession();

      try {
        window.location.replace(url);
      } catch {
        window.location.href = url;
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "We couldn't start verification. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="verify-identity-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: 520,
          bgcolor: "#ffffff",
          borderRadius: "14px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#047857",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              backgroundColor: "rgba(255,255,255,0.15)",
            }}
          >
            <VerifiedUserIcon fontSize="small" />
          </Box>

          <Typography
            id="verify-identity-title"
            variant="h6"
            sx={{ fontWeight: 700 }}
          >
            {verificationPending ? "Verification status" : "Verify your identity"}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 4, py: 3.5 }}>
          {verificationPending ? (
            <>
              <Box
                sx={{
                  bgcolor: "rgba(209, 250, 229, 0.5)",
                  border: "1px solid rgba(4, 120, 87, 0.25)",
                  borderRadius: 2,
                  p: 2.5,
                  mb: 2.5,
                  textAlign: "center",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700, color: "#065f46", mb: 1 }}>
                  Verification Pending
                </Typography>
                <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.6 }}>
                  {politicianPending
                    ? "Your identity has been verified through Stripe. Your account is now under review by our team and will be approved shortly."
                    : "Your verification has been submitted and is being reviewed by our team. You'll be notified once it's approved."}
                </Typography>
              </Box>

              <Button
                onClick={() => onClose?.()}
                variant="contained"
                fullWidth
                sx={{
                  py: 1.4,
                  fontWeight: 700,
                  borderRadius: "10px",
                  fontSize: "0.95rem",
                  backgroundColor: "#047857",
                  "&:hover": { backgroundColor: "#065f46" },
                }}
              >
                Got it
              </Button>
            </>
          ) : (
            <>
              {communityEngagement && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "#334155",
                    lineHeight: 1.6,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  {COMMUNITY_ENGAGEMENT_HINT}
                </Typography>
              )}

              <Typography
                variant="body1"
                sx={{
                  mb: 2.5,
                  color: "#1f2937",
                  lineHeight: 1.6,
                }}
              >
                Identity verification helps keep CivixThread a credible space for real civic participation.
              </Typography>

              {/* Data source section - whole line clickable */}
              <Box
                onClick={() => setShowDataModal(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowDataModal(true);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  mb: 2.5,
                  cursor: "pointer",
                  "&:hover": { opacity: 0.85 },
                }}
                aria-label="View data sources"
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#047857", flexShrink: 0 }} />

                <Typography
                  variant="body2"
                  sx={{
                    color: "#374151",
                    lineHeight: 1.5,
                  }}
                >
                  Learn how we use data and the sources we rely on.
                </Typography>

                <OpenInNewIcon fontSize="small" sx={{ color: "#047857", flexShrink: 0, ml: 0.5 }} />
              </Box>

              <DataSourcesPopupModal
                open={showDataModal}
                onClose={() => setShowDataModal(false)}
              />

              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: "#374151",
                  lineHeight: 1.6,
                }}
              >
                {"You'll be securely redirected to complete verification and then brought back."}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: "#374151",
                  lineHeight: 1.6,
                }}
              >
                {"Don't have a passport or driver's license? "}
                <Box
                  component="span"
                  onClick={() => setShowManualVerification(true)}
                  sx={{
                    color: "#047857",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": { color: "#065f46" },
                  }}
                >
                  Use alternative verification
                </Box>
              </Typography>

              <ManualVerificationModal
                isOpen={showManualVerification}
                onClose={() => setShowManualVerification(false)}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Actions */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Button
                  onClick={handleVerify}
                  variant="contained"
                  disabled={loading}
                  aria-label="Start identity verification"
                  sx={{
                    py: 1.4,
                    fontWeight: 700,
                    borderRadius: "10px",
                    fontSize: "0.95rem",
                    backgroundColor: "#047857",
                    "&:hover": {
                      backgroundColor: "#065f46",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Verify now"
                  )}
                </Button>

                <Button
                  onClick={() => onClose?.()}
                  variant="outlined"
                  sx={{
                    py: 1.2,
                    borderRadius: "10px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderColor: "#000",
                    color: "#000",
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: "#000",
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  Not now
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default VerifyIdentityModal;
